import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { Box, Table, type TableProps } from '@mui/material';
import { DatagridConfigurable, useResourceContext, type DatagridConfigurableProps } from 'react-admin';
import { useAdminText } from './useAdminText';

/** Give custom domain renderers explicit table metadata without duplicating formatting. */
export function GridColumn({ children }: { children: ReactNode; label: string; source?: string; sortable?: boolean }) {
  return <>{children}</>;
}

/** Both adapters share MUI table tokens; neither owns fetching, sorting or filtering. */
export function DataRegion({ children, label }: { children: ReactNode; label: string }) {
  return <Box role="region" aria-label={label} tabIndex={0} sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto', borderRadius: 'inherit', '&:focus-visible': { outlineOffset: -3 } }}>{children}</Box>;
}

export function AdminDataGrid({ children, ...props }: DatagridConfigurableProps) {
  const resource = useResourceContext();
  const t = useAdminText();
  return <DataRegion label={t('tableRegion', 'Results table')}>
    <DatagridConfigurable bulkActionButtons={false} size="small" {...props} preferenceKey={`${resource}.datagrid`}>
      {Children.map(children, child => {
        if (!isValidElement(child)) return child;
        const field = child as ReactElement<{ source?: string; sortable?: boolean }>;
        return !field.props.source && field.props.sortable === undefined ? cloneElement(field, { sortable: false }) : child;
      })}
    </DatagridConfigurable>
  </DataRegion>;
}

/** For operational views with explicit server/cursor state or small complete datasets. */
export function AdminTable({ children, 'aria-label': label, ...props }: TableProps) {
  const t = useAdminText();
  return <DataRegion label={label ?? t('tableRegion', 'Results table')}><Table size="small" aria-label={label} {...props}>{children}</Table></DataRegion>;
}
